import { Base64 } from 'js-base64';
import * as type from './type-utils';

/**
 * 空函数, 用于默认赋值
 */
export function noop() {}

/**
 * 延迟, 用于 async await 等待
 * 用例:
 * (async ()=>{
 *   console.log('等待1s');
 *   await delay(1000);
 *   console.log('完成');
 * })();
 * @param ms
 */
export function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * 移除空白spaces
 * 输入: 137 2700 3240
 * 输出: 13727003240
 * @param text
 * @return {*}
 */
export function removeSpaces(text) {
  if (!text) {
    return text;
  }
  return text.replace(/\D+/g, '');
}

/**
 * 创建数组分组
 * @example
 * var arr = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14];
 * var groupedArr = createGroupedArray(arr, 4);
 * var result = JSON.stringify(groupedArr);
 * // result: "[[1,2,3,4],[5,6,7,8],[9,10,11,12],[13,14]]"
 * @return {Array}
 */
export function createGroupedArray(arr, chunkSize) {
  let groups = [],
    i = 0,
    arrLength = arr.length;
  for (; i < arrLength; i += chunkSize) {
    groups.push(arr.slice(i, i + chunkSize));
  }
  return groups;
}

/**
 * 仿 C# String.Format 实现
 * @example
 * 输入: stringFormat('{0} is dead, but {1} is alive!', 'ASP', 'ASP.NET');
 * 输出: ASP is dead, but ASP.NET is alive!
 * @param format
 * @return {*}
 */
export function stringFormat(format) {
  const args = Array.prototype.slice.call(arguments, 1);
  return format.replace(/{(\d+)}/g, function(match, number) {
    return typeof args[number] !== 'undefined' ? args[number] : match;
  });
}

/**
 * 对象转换成 json string
 * @param input
 * @return {*}
 */
function object2JSONString(input) {
  if (!type.isNullOrUndefined(input)) {
    let str = input;
    if (type.isObjectLike(str) || type.isString(str)) {
      str = JSON.stringify(str);
      return str;
    }
  }
  throw new Error('the type of input should be {object} or {string}');
}

/**
 * base 64 url 编码
 * @param input
 */
export function base64UrlEncode(input) {
  const str = object2JSONString(input);
  return Base64.toBase64(str, true);
}

/**
 * base 64 url 解码
 * @param str
 * @param jsonParse
 * @return {*}
 */
export function base64UrlDecode(str, jsonParse = true) {
  const result = Base64.fromBase64(str);
  if (jsonParse) {
    return JSON.parse(result);
  }
  return result;
}

export function transformObjectToSearchString(obj) {
  return Object.keys(obj)
    .reduce((str, currentKey) => (str += `${currentKey}=${encodeURIComponent(obj[currentKey])}&`), '?')
    .slice(0, -1);
}

/**
 * 模拟a标签跳转
 * @param url
 * @param target
 * @return {*}
 */
export function aLink(url, target = '_blank') {
  const a = document.createElement('a');
  a.href = url;
  a.target = target;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/**
 * 页面跳转
 */
export function jumpPage(url, params = null, newStatus = false) {
  let fullPath = window.location.origin + window.location.pathname;
  // TODO TEST
  // let fullPath = 'https://test-zt.xoyo.com/mechabreak.seasungames.cn/p/zt/2025/03/20/index/news-detail.html';
  let urlWithoutFileName = fullPath.replace(/\/[^\/]+\.html$/, '/');
  // 如果路径末尾没有斜杠，则添加斜杠（可选）
  if (!urlWithoutFileName.endsWith('/')) {
    urlWithoutFileName += '/';
  }
  let nextUrl = urlWithoutFileName + url;
  if (params) {
    nextUrl += `${params}`;
  }

  // console.log('nextUrl', nextUrl);
  // return;
  if (newStatus) {
    aLink(nextUrl);
  } else {
    window.location.href = nextUrl;
  }
}

/**
 * 跳转页面
 * @param link 路由链接
 * @param isHref 是否为外链
 */
export function toPage(link, isHref, redirectUtil = noop) {
  const linkMap = { '': 'index' };
  const htmlLink = linkMap[link] || link;
  if (isHref) {
    window.open(link);
  } else if (!IS_DEV_ENV) {
    const url = `${htmlLink}.html`;
    jumpPage(url, '#/');
  } else {
    redirectUtil('/' + link);
  }
}

/**
 * 判断数据类型
 * @param obj {any}
 * @param type Array|Object|String|Number|Function|Boolean|Null|Undefined
 */
export function isType(obj, type) {
  return Object.prototype.toString.call(obj) === `[object ${type}]`;
}

/**
 * 复制文本到剪贴板
 * @param {string} text - 需要复制的文本内容
 * @returns {Promise<boolean>} - 返回复制是否成功的Promise
 * @description
 * 该函数提供两种复制方案:
 * 1. 优先使用现代浏览器的 Clipboard API
 * 2. 如果不支持 Clipboard API,则降级使用 execCommand 方案
 * @example
 * // 复制文本
 * await copyToClipboard('要复制的文本');
 */
export async function copyToClipboard(text) {
  // 优先使用现代浏览器的 Clipboard API
  if (navigator.clipboard) {
    await navigator.clipboard.writeText(text);
    return true;
  }

  // 降级方案:创建临时textarea元素并执行复制命令
  const textarea = document.createElement('textarea');
  textarea.value = text;
  document.body.appendChild(textarea);
  textarea.select();
  const success = document.execCommand('copy');
  document.body.removeChild(textarea);

  if (!success) throw new Error('拷贝失败');
  return success;
}

/**
 * 获取url参数
 * @param {*} name 参数名
 * @param {*} url
 * @returns
 */
export const getParameterByName = function(name) {
  const url = window.location.href;
  name = name.replace(/[[]]/g, '$&');
  const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
    results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return '';
  return decodeURIComponent(results[2].replace(/\\+/g, ' '));
};
